import { createStore } from "zustand/vanilla";
import { useStore } from "zustand";
import {
  bootstrapSession,
  getCurrentAdminAccess,
  getActiveBanNotice,
  onAuthStateChange,
  signOut as signOutFromAuth,
} from "@/features/auth/api/authClient";
import {
  getProfileByUserId,
  updateCurrentProfile,
} from "@/features/profile/api/profileClient";
import { setLocale } from "@/i18n";
import type {
  AuthSnapshot,
  AuthStatus,
  ProfileRecord,
  ProfileUpdateInput,
  ProtectedRouteDecision,
  PublicRouteDecision,
} from "@/types/auth";

type AuthListener = ReturnType<typeof onAuthStateChange>;

export interface AuthStoreDependencies {
  bootstrapSession: typeof bootstrapSession;
  getCurrentAdminAccess: typeof getCurrentAdminAccess;
  getActiveBanNotice: typeof getActiveBanNotice;
  onAuthStateChange: typeof onAuthStateChange;
  getProfileByUserId: typeof getProfileByUserId;
  updateCurrentProfile: typeof updateCurrentProfile;
  signOut: typeof signOutFromAuth;
  setLocale: typeof setLocale;
}

export interface AuthStoreState extends AuthSnapshot {
  authListener: AuthListener | null;
  hydrate: () => Promise<void>;
  syncSession: (session: AuthSnapshot["session"]) => Promise<void>;
  refreshProfile: () => Promise<ProfileRecord | null>;
  refreshBanNotice: () => Promise<void>;
  saveProfile: (input: ProfileUpdateInput) => Promise<ProfileRecord>;
  clearError: () => void;
  logOut: () => Promise<void>;
}

const defaultDependencies: AuthStoreDependencies = {
  bootstrapSession,
  getCurrentAdminAccess,
  getActiveBanNotice,
  onAuthStateChange,
  getProfileByUserId,
  updateCurrentProfile,
  signOut: signOutFromAuth,
  setLocale,
};

function getAuthStatus(session: AuthStoreState["session"]): AuthStatus {
  if (!session?.user) {
    return "anonymous";
  }

  return session.user.email_confirmed_at ? "authenticated" : "unverified";
}

function isProfileUnavailable(profile: ProfileRecord | null) {
  return Boolean(
    profile?.deleted_at
      || (profile?.deletion_requested_at && !profile.deletion_cancelled_at),
  );
}

export function getProtectedRouteDecision(
  snapshot: Pick<AuthSnapshot, "authStatus" | "banStatus" | "isHydrated">,
): ProtectedRouteDecision {
  if (
    !snapshot.isHydrated
    || snapshot.authStatus === "idle"
    || snapshot.authStatus === "loading"
    || (snapshot.authStatus === "authenticated" && snapshot.banStatus === "loading")
  ) {
    return "pending";
  }

  if (snapshot.authStatus === "authenticated" && snapshot.banStatus !== "banned") {
    return "allow";
  }

  if (snapshot.authStatus === "unverified") {
    return "verify";
  }

  return "login";
}

export function getPublicRouteDecision(snapshot: Pick<AuthSnapshot, "authStatus" | "isHydrated">): PublicRouteDecision {
  if (!snapshot.isHydrated || snapshot.authStatus === "idle" || snapshot.authStatus === "loading") {
    return "pending";
  }

  if (snapshot.authStatus === "authenticated") {
    return "app";
  }

  if (snapshot.authStatus === "unverified") {
    return "verify";
  }

  return "allow";
}

async function loadAndApplyProfile(
  userId: string,
  deps: AuthStoreDependencies,
  set: (partial: Partial<AuthStoreState>) => void,
) {
  set({ profileStatus: "loading", authError: null });

  try {
    const profile = await deps.getProfileByUserId(userId);
    let isAdmin = false;

    try {
      isAdmin = await deps.getCurrentAdminAccess();
    } catch {
      isAdmin = false;
    }

    await deps.setLocale(profile?.language_pref ?? "en");
    set({
      isAdmin,
      profile,
      profileStatus: "ready",
      authError: null,
    });
    return profile;
  } catch (error) {
    await deps.setLocale("en");
    set({
      isAdmin: false,
      profile: null,
      profileStatus: "error",
      authError: error instanceof Error ? error.message : "Profile load failed.",
    });
    return null;
  }
}

async function syncBanNotice(
  deps: AuthStoreDependencies,
  get: () => AuthStoreState,
  set: (partial: Partial<AuthStoreState>) => void,
) {
  const currentUser = get().user;

  if (get().authStatus !== "authenticated" || !currentUser) {
    set({
      banNotice: null,
      banStatus: "idle",
    });
    return;
  }

  set({
    banStatus: "loading",
  });

  try {
    const banNotice = await deps.getActiveBanNotice(currentUser);
    if (banNotice?.is_banned) {
      await deps.signOut();
      await deps.setLocale("en");
      set({
        authError: null,
        authStatus: "anonymous",
        banNotice,
        banStatus: "banned",
        isAdmin: false,
        isHydrated: true,
        profile: null,
        profileStatus: "idle",
        session: null,
        user: null,
      });
      return;
    }

    set({
      banNotice: null,
      banStatus: "clear",
    });
  } catch {
    set({
      banNotice: null,
      banStatus: "clear",
    });
  }
}

export function createAuthStore(deps: AuthStoreDependencies = defaultDependencies) {
  return createStore<AuthStoreState>((set, get) => ({
    session: null,
    user: null,
    profile: null,
    authStatus: "idle",
    profileStatus: "idle",
    banStatus: "idle",
    banNotice: null,
    isAdmin: false,
    isHydrated: false,
    authError: null,
    authListener: null,

    hydrate: async () => {
      if (!get().authListener) {
        const listener = deps.onAuthStateChange(async (session) => {
          await get().syncSession(session);
        });

        set({ authListener: listener });
      }

      set({
        authStatus: "loading",
        banStatus: "idle",
        profileStatus: "idle",
        authError: null,
      });

      const session = await deps.bootstrapSession();
      await get().syncSession(session);
    },

    syncSession: async (session) => {
      const authStatus = getAuthStatus(session);

      set({
        session,
        user: session?.user ?? null,
        authStatus,
        isHydrated: true,
      });

      if (authStatus === "authenticated" && session?.user) {
        const profile = await loadAndApplyProfile(session.user.id, deps, (partial) => set(partial));
        if (isProfileUnavailable(profile)) {
          await deps.signOut();
          await deps.setLocale("en");
          set({
            authError: null,
            authStatus: "anonymous",
            banNotice: null,
            banStatus: "idle",
            isAdmin: false,
            isHydrated: true,
            profile: null,
            profileStatus: "idle",
            session: null,
            user: null,
          });
          return;
        }
        await syncBanNotice(deps, get, (partial) => set(partial));
        return;
      }

      await deps.setLocale("en");
      set({
        banNotice: null,
        banStatus: "idle",
        isAdmin: false,
        profile: null,
        profileStatus: "idle",
        authError: null,
      });
    },

    refreshProfile: async () => {
      const userId = get().user?.id;
      if (!userId) {
        set({
          isAdmin: false,
          profile: null,
          profileStatus: "idle",
        });
        return null;
      }

      return loadAndApplyProfile(userId, deps, (partial) => set(partial));
    },

    refreshBanNotice: async () => {
      await syncBanNotice(deps, get, (partial) => set(partial));
    },

    saveProfile: async (input) => {
      set({ profileStatus: "loading", authError: null });
      const profile = await deps.updateCurrentProfile(input);
      let isAdmin = false;

      try {
        isAdmin = await deps.getCurrentAdminAccess();
      } catch {
        isAdmin = false;
      }

      await deps.setLocale(profile.language_pref);
      set({
        isAdmin,
        profile,
        profileStatus: "ready",
      });
      return profile;
    },

    clearError: () => {
      set({ authError: null });
    },

    logOut: async () => {
      await deps.signOut();
      await deps.setLocale("en");
      set({
        session: null,
        user: null,
        profile: null,
        authStatus: "anonymous",
        profileStatus: "idle",
        banStatus: "idle",
        banNotice: null,
        isAdmin: false,
        isHydrated: true,
        authError: null,
      });
    },
  }));
}

export const authStore = createAuthStore();

export function useAuthStore<T>(selector: (state: AuthStoreState) => T) {
  return useStore(authStore, selector);
}
